import React from 'react';
import { Lightbulb, BookOpen, Hand, Trophy, Gift, Sparkles } from 'lucide-react';

// Phase definitions with visual styling
const LESSON_PHASES = {
  hook: {
    name: 'Hook',
    icon: Lightbulb,
    emoji: '💡',
    color: '#FBBF24', // amber
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    description: 'Get ready to discover!',
    blockTypes: ['story', 'narration', 'video', 'text'],
  },
  teach: {
    name: 'Learn',
    icon: BookOpen,
    emoji: '📚',
    color: '#60A5FA', // blue
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    description: 'Time to learn something new!',
    blockTypes: ['text', 'story', 'image', 'video', 'visual'],
  },
  guided_practice: {
    name: 'Practice',
    icon: Hand,
    emoji: '✋',
    color: '#4ADE80', // green
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    description: 'Try it with some help!',
    blockTypes: ['matching', 'drag_drop', 'fill_blank', 'sorting', 'sequence', 'worked_example'],
  },
  // Alias for backend compatibility
  guided_examples: {
    name: 'Practice',
    icon: Hand,
    emoji: '✋',
    color: '#4ADE80', // green
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    description: 'Try it with some help!',
    blockTypes: ['matching', 'drag_drop', 'fill_blank', 'sorting', 'sequence', 'worked_example'],
  },
  challenge: {
    name: 'Challenge',
    icon: Trophy,
    emoji: '🏆',
    color: '#F472B6', // pink
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    description: 'Show what you learned!',
    blockTypes: ['question', 'logic_puzzle', 'true_false', 'quick_recall', 'challenge'],
  },
  reward: {
    name: 'Celebrate',
    icon: Gift,
    emoji: '🎁',
    color: '#A78BFA', // purple
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    description: 'Great job!',
    blockTypes: ['reflection', 'creative_writing', 'drawing', 'music', 'rhythm'],
  },
};

// Determine which phase a block belongs to based on its type or explicit phase field
export const getPhaseForBlock = (blockType, blockIndex, totalBlocks, explicitPhase = null) => {
  // If an explicit phase is provided, use it directly
  if (explicitPhase && LESSON_PHASES[explicitPhase]) {
    return { phaseId: explicitPhase, ...LESSON_PHASES[explicitPhase] };
  }
  
  // Map some block types to their phases
  const blockTypeToPhase = {
    'worked_example': 'guided_practice',
    'visual': 'teach',
  };
  
  if (blockTypeToPhase[blockType]) {
    const phaseId = blockTypeToPhase[blockType];
    return { phaseId, ...LESSON_PHASES[phaseId] };
  }
  
  // Check if block type matches a specific phase
  for (const [phaseId, phase] of Object.entries(LESSON_PHASES)) {
    if (phase.blockTypes.includes(blockType)) {
      return { phaseId, ...phase };
    }
  }
  
  // Fallback: estimate phase by position in lesson
  const percentComplete = blockIndex / totalBlocks;
  if (percentComplete < 0.2) return { phaseId: 'hook', ...LESSON_PHASES.hook };
  if (percentComplete < 0.4) return { phaseId: 'teach', ...LESSON_PHASES.teach };
  if (percentComplete < 0.6) return { phaseId: 'guided_practice', ...LESSON_PHASES.guided_practice };
  if (percentComplete < 0.8) return { phaseId: 'challenge', ...LESSON_PHASES.challenge };
  return { phaseId: 'reward', ...LESSON_PHASES.reward };
};

// Visual phase indicator component
const LessonPhaseIndicator = ({ blockType, blockIndex, totalBlocks, explicitPhase = null, showDescription = true }) => {
  const phase = getPhaseForBlock(blockType, blockIndex, totalBlocks, explicitPhase);
  const Icon = phase.icon;
  
  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-6 ${phase.bgColor} border ${phase.borderColor}`}
      data-testid={`phase-indicator-${phase.phaseId}`}
    >
      {/* Phase icon */}
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${phase.color}30` }}
      >
        <span className="text-xl">{phase.emoji}</span>
      </div>
      
      {/* Phase info */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span 
            className="text-sm font-semibold uppercase tracking-wider"
            style={{ color: phase.color }}
          >
            {phase.name}
          </span>
        </div>
        {showDescription && (
          <p className="text-slate-400 text-sm">{phase.description}</p>
        )}
      </div>
      
      {/* Sparkle decoration */}
      <Sparkles className="w-5 h-5 text-slate-500 animate-pulse" />
    </div>
  );
};

// Progress indicator showing all 5 phases
export const LessonPhaseProgress = ({ currentPhase, completedPhases = [] }) => {
  const phases = ['hook', 'teach', 'guided_practice', 'challenge', 'reward'];
  
  return (
    <div className="flex items-center justify-between gap-2 mb-6" data-testid="phase-progress">
      {phases.map((phaseId, idx) => {
        const phase = LESSON_PHASES[phaseId];
        const isActive = phaseId === currentPhase;
        const isCompleted = completedPhases.includes(phaseId);
        
        return (
          <React.Fragment key={phaseId}>
            <div 
              className={`flex flex-col items-center transition-all ${
                isActive ? 'scale-110' : ''
              }`}
            >
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isCompleted 
                    ? 'bg-green-500' 
                    : isActive 
                      ? `ring-2 ring-offset-2 ring-offset-slate-900`
                      : 'bg-slate-700'
                }`}
                style={{ 
                  backgroundColor: isActive ? `${phase.color}30` : undefined,
                  ringColor: isActive ? phase.color : undefined,
                }}
              >
                <span className={`text-lg ${isCompleted || isActive ? '' : 'opacity-40'}`}>
                  {phase.emoji}
                </span>
              </div>
              <span 
                className={`text-xs mt-1 font-medium ${
                  isActive ? '' : 'text-slate-500'
                }`}
                style={{ color: isActive ? phase.color : undefined }}
              >
                {phase.name}
              </span>
            </div>
            
            {/* Connector line */}
            {idx < phases.length - 1 && (
              <div 
                className={`flex-1 h-0.5 ${
                  completedPhases.includes(phases[idx + 1]) 
                    ? 'bg-green-500' 
                    : 'bg-slate-700'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LessonPhaseIndicator;
